import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert } from '@mui/material';
import api from '../api/axiosConfig';
import Footer from '../components/Footer';

// Google Analytics gtag function and window property typings
const GA_MEASUREMENT_ID = 'G-56R16ZWNK6';
function gtag(...args: any[]) {
  (window as any).dataLayer.push(args);
}
function injectGtag() {
  if (!(window as any).gtagScriptInjected) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);
    (window as any).gtagScriptInjected = true;
  }
}

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    injectGtag();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!email) {
      setError('Email is required.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/request-pass', { email });
      setSuccess('If this email is registered, you will receive a password reset link.');
    } catch (err: any) {
      let message = err.response?.data?.ErrorMessage || err.response?.data?.message || 'Failed to send reset email.';
      if (err.message && message === 'Failed to send reset email.') {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      minHeight="100vh"
      width="100vw"
      sx={{ background: '#fff' }}
    >
      {/* Left image section */}
      <Box
        flex={1}
        display={{ xs: 'none', md: 'flex' }}
        alignItems="center"
        justifyContent="center"
        sx={{
          background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)',
          minHeight: '100vh',
          width: '50vw',
          p: 0,
          m: 0,
          overflow: 'hidden',
          position: { md: 'fixed' },
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          zIndex: 1,
        }}
      >
        <img
          src="/paas.png"
          alt="Prediction as a Service"
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 0, boxShadow: 'none' }}
          onError={e => { (e.target as HTMLImageElement).src = '/logo_black.png'; }}
        />
      </Box>
      {/* Right forgot-password section */}
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={{ xs: '100vw', md: '50vw' }}
        minHeight="100vh"
        sx={{ background: '#fff', ml: { md: '50vw' }, zIndex: 2 }}
      >
        <Paper elevation={3} sx={{ p: 6, maxWidth: 500, width: '100%' }}>
          <Typography variant="h5" mb={2} align="center">Forgot Password</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Email'}
            </Button>
          </form>
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Link to="/login">Back to Login</Link>
            <Link to="/reset-password">Reset Password</Link>
          </Box>
        </Paper>
      </Box>
      <Footer />
    </Box>
  );
};

export default ForgotPassword; 